/**
 * Provider-agnostic, surface-independent read helper for a meeting's
 * recurring series.
 *
 * Given a meeting, resolves the OTHER occurrences that share its
 * `ahoy_recurring_series_id`, each paired with its linked meeting-notes
 * summary (or `null` when none exists). The pure functions below have no
 * React dependency so a second UI surface (e.g. a recurrence badge) can
 * reuse them directly and unit-test them without rendering. Occurrences are
 * returned most-recent-first, with the current meeting excluded.
 */

import { useMemo } from 'react';

import type {
  CrmObject,
  CrmObjectFilters,
  CrmObjectsListResponse,
} from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import type { CrmObjectListOptions } from '../../query/queries';
import { useCrmObjectList } from '../../store/hooks';
import { getMeetingSeriesId } from '../../utils/recurringSeries';

export { getMeetingSeriesId };

const MEETING_SERIES_PROPERTIES = [
  'ahoy_title',
  'ahoy_start_time',
  'ahoy_recurring_series_id',
  // Accent color for the meeting card's colored bar (parity with Upcoming meetings).
  'ahoy_color',
  'ahoy_origin',
];
const MEETING_NOTES_ASSOC = 'ahoy_meeting_to_meeting_notes';
/**
 * Association linking a meeting to its meeting-prep entity. We only need to know
 * whether it exists (mirroring the `meeting_prep_status` highlight), so no target
 * properties are hydrated.
 */
const MEETING_PREP_ASSOC = 'ahoy_meeting_to_meeting_prep';
const NOTE_PROPERTIES = ['ahoy_title', 'ahoy_summary', 'ahoy_created_at'];
const SERIES_LIMIT = 50;
const INCLUDE_ASSOCIATIONS: NonNullable<
  CrmObjectListOptions['include_associations']
> = {
  [MEETING_NOTES_ASSOC]: { properties: NOTE_PROPERTIES },
  [MEETING_PREP_ASSOC]: { properties: [] },
};

export interface MeetingSeriesOccurrence {
  meeting: CrmObject;
  summary: CrmObject | null;
}

/**
 * Reads the meeting's `ahoy_is_recurring` property. The stored value is the
 * string `"true"`/`"false"`; anything else (including undefined) is false.
 */
export function isMeetingRecurring(meeting: CrmObject): boolean {
  return (
    getPropertyValue<string>(meeting.properties?.ahoy_is_recurring) === 'true'
  );
}

/**
 * Groups a series list response into occurrences (excluding the current
 * meeting), each paired with its linked meeting-notes summary. Sorted
 * most-recent-first by `ahoy_start_time`; occurrences with a missing or
 * invalid start time sort last.
 */
export function groupSeriesOccurrences(
  currentMeeting: CrmObject,
  response: CrmObjectsListResponse | undefined
): MeetingSeriesOccurrence[] {
  if (!response) {
    return [];
  }

  const associated = response.associated_objects ?? {};

  const occurrences: MeetingSeriesOccurrence[] = response.results
    .filter(m => m.entity_id !== currentMeeting.entity_id)
    .map(m => {
      const notesAssoc = m.associations?.find(
        a => a.association_type_name === MEETING_NOTES_ASSOC
      );
      const summary = notesAssoc
        ? (associated[notesAssoc.target_entity_id] ?? null)
        : null;
      return { meeting: m, summary };
    });

  const startTimeMs = (occurrence: MeetingSeriesOccurrence): number => {
    const value = getPropertyValue<string>(
      occurrence.meeting.properties?.ahoy_start_time
    );
    const ms = value ? Date.parse(value) : NaN;
    return Number.isNaN(ms) ? -Infinity : ms;
  };

  // The fetch requests SERIES_LIMIT + 1 rows so that removing the current
  // meeting still leaves up to SERIES_LIMIT other occurrences. Cap the result
  // here in case the current meeting was not among the returned rows.
  return occurrences
    .sort((a, b) => startTimeMs(b) - startTimeMs(a))
    .slice(0, SERIES_LIMIT);
}

/** Parsed `ahoy_start_time` in ms since epoch; `NaN` when missing/invalid. */
export function occurrenceStartMs(occurrence: MeetingSeriesOccurrence): number {
  const value = getPropertyValue<string>(
    occurrence.meeting.properties?.ahoy_start_time
  );
  return value ? Date.parse(value) : NaN;
}

/** Whether an occurrence has a linked summary with non-empty text. */
export function occurrenceHasSummary(
  occurrence: MeetingSeriesOccurrence
): boolean {
  if (!occurrence.summary) {
    return false;
  }
  const text = getPropertyValue<string>(
    occurrence.summary.properties?.ahoy_summary
  )?.trim();
  return !!text;
}

/**
 * Whether an occurrence has a meeting-prep entity, detected by the presence of
 * an `ahoy_meeting_to_meeting_prep` association on the meeting (existence is the
 * signal, matching the `meeting_prep_status` highlight).
 */
export function occurrenceHasMeetingPrep(
  occurrence: MeetingSeriesOccurrence
): boolean {
  return (occurrence.meeting.associations ?? []).some(
    a => a.association_type_name === MEETING_PREP_ASSOC
  );
}

export interface SeriesOccurrenceFilter {
  /** Inclusive lower bound (ms since epoch); `undefined` = no lower bound. */
  startMs?: number;
  /** Exclusive upper bound (ms since epoch); `undefined` = no upper bound. */
  endMs?: number;
  /** When true, drop occurrences without a (non-empty) summary. */
  onlyWithSummaries?: boolean;
}

/**
 * Applies the section's client-side filters to the (already sorted,
 * most-recent-first) occurrences, preserving order. Occurrences with a
 * missing/invalid start time are excluded whenever a date bound is set.
 */
export function filterSeriesOccurrences(
  occurrences: MeetingSeriesOccurrence[],
  { startMs, endMs, onlyWithSummaries }: SeriesOccurrenceFilter
): MeetingSeriesOccurrence[] {
  const hasDateBound = startMs !== undefined || endMs !== undefined;
  return occurrences.filter(occurrence => {
    if (onlyWithSummaries && !occurrenceHasSummary(occurrence)) {
      return false;
    }
    if (hasDateBound) {
      const ms = occurrenceStartMs(occurrence);
      if (Number.isNaN(ms)) {
        return false;
      }
      if (startMs !== undefined && ms < startMs) {
        return false;
      }
      if (endMs !== undefined && ms >= endMs) {
        return false;
      }
    }
    return true;
  });
}

/**
 * Returns the other occurrences of a meeting's recurring series (current
 * meeting excluded), most-recent-first, each paired with its linked summary.
 */
export function useMeetingSeries(orgId: number, meeting: CrmObject) {
  const seriesId = getMeetingSeriesId(meeting);
  const isRecurring = isMeetingRecurring(meeting);

  const filters = useMemo<CrmObjectFilters>(
    () => ({
      properties: MEETING_SERIES_PROPERTIES,
      sort: 'ahoy_start_time',
      order: 'desc',
      // Fetch one extra row: the current meeting shares this series id and is
      // filtered out client-side, so requesting SERIES_LIMIT + 1 keeps up to
      // SERIES_LIMIT other occurrences available.
      limit: SERIES_LIMIT + 1,
      ...(seriesId
        ? {
            filter_groups: [
              {
                filters: [
                  {
                    property: 'ahoy_recurring_series_id',
                    operator: 'exact',
                    value: seriesId,
                  },
                ],
              },
            ],
          }
        : {}),
    }),
    [seriesId]
  );

  const options = useMemo(
    () => ({
      enabled: !!seriesId && !!orgId,
      include_associations: INCLUDE_ASSOCIATIONS,
    }),
    [seriesId, orgId]
  );

  const { data, isLoading, error, refetch } = useCrmObjectList(
    orgId,
    'ahoy_meeting',
    filters,
    options
  );

  const occurrences = useMemo(
    () => groupSeriesOccurrences(meeting, data),
    [meeting, data]
  );

  return { seriesId, isRecurring, occurrences, isLoading, error, refetch };
}
